<template>
    <div class="dashboard_classification">
        <div class="main_div">
            <ClassificationHeader
                ref="classificationHeader"
                :typeOptions="typeOptions"
                :periodDataSelect="periodDataSelect"
                :localTypeFiles="localTypeFiles"
                :selectedType="selectedType"
                :selectedRange="selectedRange"
                :selectedUploadFile="selectedUploadFile"
                :searchFilter="searchFilter"
                :filterTable="filterTable"
                :filterTableHasAccrual="filterTableHasAccrual"
                :allFilterCategories="allFilterCategories"
                :allAccountsOnDocs="allAccountsOnDocs"
                :getUrlDriveClassification="getUrlDriveClassification"
                :isDownloadingFile="isDownloadingFile"
                @update:selectedType="onSelectedType"
                @update:selectedRange="onSelectedRange"
                @update:selectedUploadFile="(v) => selectedUploadFile = v"
                @update:searchFilter="(v) => searchFilter = v"
                @update:filterTable="(v) => filterTable = v"
                @update:filterTableHasAccrual="(v) => filterTableHasAccrual = v"
                @download="downloadFile(getUrlDriveClassification, 'Data clasificada.xlsx')"
                @selected-categories="updateSelectedCategories"
                @selected-accounts="updateSelectedAccounts"
            />

            <div class="underHeader">


                <CounterpartTable
                    ref="counterpartTable"
                    class="mb-5"
                    :entries="getPaginatedList"
                    :categories="categories"
                    :accountFilters="accountFilters"
                    :categoriesFilter="categoriesFilter"
                    :sortColumn="sortColumn"
                    :sortDirection="sortDirection"
                    :siiSyncing="siiSyncing"
                    :maxHeight="tableMaxHeight"
                    :read-only="isConsolidatedCompany"
                    :show-owner="isConsolidatedCompany"
                    @sort="changeOrderGeneric"
                    @open="openAccountByCompanyModal"
                    @clear-filter="clearFiltered"
                    @delete-filter="deleteItemFilter"
                />
                <Pagination ref="pagination" v-model="actualPage" :maxPages="maxPages" />
            </div>
        <ResumeClassificationModal
            :active.sync="showFirstTimeModal"
            :typeOptions="typeOptions"
            :periodDataSelect="periodDataSelect"
            @reload-counterpart="reloadAfterClassification"
        />
        </div>
    </div>
</template>

<script>
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/900.css";

import Pagination from "@/components/generics/Pagination";
import CounterpartTable from "@/components/classification/CounterpartTable.vue";
import ClassificationHeader from "@/components/classification/ClassificationHeader.vue";
import ClassifyAccountAttributes from "@/components/modals/ClassifyAccountAttributes";
import ModalFullHeightRight from "@/components/modals/ModalFullHeightRight";
import BodyUploadDocs from "@/components/uploadDocuments/modalParts/bodyUploadDocs";
import HeaderUploadDocs from "@/components/uploadDocuments/modalParts/headerUploadDocs";
import ResumeClassificationModal from "@/components/modals/ResumeClassificationModal";
import { isDismissedUntilEndOfDay } from "@/utils/modalDismissal";
import { isUserSiiSyncing } from "@/utils/sii";
import { toIsoDay } from "@/utils/isoDay";
import { resolveClassifierRange } from "@/utils/classifyDeepLink";
import { isReadOnlyClassification } from "@/utils/counterpartObligation";
import { downloadClassificationFile } from "@/utils/downloadClassificationFile";
import { mapActions, mapState } from "vuex";
import { consoleHandlerError, notificate } from "../utils/message";

// Mientras el robot "obteniendo datos del SII" está visible no hay nada que
// recargue las contrapartes: sin esto el usuario tenía que refrescar a mano.
// Hacemos polling cada 2 min hasta que lleguen contrapartes o termine el sync.
const SII_SYNC_POLL_MS = 2 * 60 * 1000;

// Espera antes de mandar la búsqueda al servidor: filtrar dejó de ser local.
const SEARCH_DEBOUNCE_MS = 350;

// La tabla de contrapartes llena el alto libre del viewport y scrollea
// internamente, sin scroll de página. El cap (`max-height`) se mide en runtime
// restando la paginación y un respiro inferior; se recalcula al cambiar el
// viewport o la altura del header (los pills de filtro lo agrandan y desplazan
// el top de la tabla). `max-height` —no `height`— deja la tabla content-sized
// cuando hay pocas filas y sólo scrollea cuando excede el alto disponible.
const TABLE_MIN_HEIGHT = 220; // piso usable en viewports muy cortos
const TABLE_BELOW_GAP = 48; // mb-5 entre tabla y paginación + respiro inferior

export default {
    name: "DashboardClassificator",
    components: {
        Pagination,
        ResumeClassificationModal,
        CounterpartTable,
        ClassificationHeader,
    },
    data() {
        return {
            showFirstTimeModal: false,
            hasShownFirstTimeModal: false,
            countDirectionOrder: 0,
            sortColumn: "order",
            sortDirection: "asc",
            modalDefault: {
                parent: this,
                width: "99%",
                hasModalCard: true,
                canCancel: ["escape", "outside"],
                customClass: "center-always-modal",
            },
            searchFilter: "",
            localTypeFiles: [],
            selectedUploadFile: "",
            filterTable: "",
            filterTableHasAccrual: '',
            actualPage: 1,
            itemsPerPage: 20,
            // Total de contrapartes del servidor: manda en el paginador.
            totalCounterparts: 0,
            searchTimer: null,
            allAccountsOnDocs: [],
            allFilterCategories: [],
            accountFilters: [],
            categoriesFilter: [],
            isDownloadingFile: false,
            siiSyncPoll: null,
            tableMaxHeight: null,
            // Esta vista y DashboardErp comparten el módulo Vuex `classification`
            // (mismo slot allOrderedCounterparts). Al navegar entre ambas (v-if en
            // Dashboard.vue) la vista vieja se destruye, pero su cadena async
            // `getAllAccounts().then(...)` o un tick del polling SII puede resolver
            // DESPUÉS y disparar un fetch que pisa los datos de la vista
            // ya montada. Este flag corta ese fetch tardío (ver getAllPaginatedCounterparts).
            isActive: true,
        };
    },
    mounted() {
        // Comparte el slot de contrapartes con el Libro Mayor: limpiar al montar
        // evita mostrar las del ERP hasta que llega el fetch del clasificador.
        this.clearCounterparts();
        this.initClassificationFilters();
        this.localTypeFiles = this.typeFiles.filter(typeFile =>{
            return this.user.valid_counterparts.length == 0 || this.user.valid_counterparts.includes(typeFile.counterpartType)
        })
        this.getAllAccounts().then(() => {
            this.onSelectedRange('last12') // here load all docs on watch
            // Deep-link desde el drawer de reportes (?classify_*): corre en
            // paralelo a la carga de la lista — despacha fetchCounterpart,
            // no depende de allOrderedCounterparts.
            this.consumeClassifyDeepLink()
        })

        this.changeColumn("total_3_months");
        this.sortData("total_3_months")
        this.changeColumn("total_3_months");
        this.sortData("total_3_months")

        this.$nextTick(this.measureTableHeight);
        window.addEventListener("resize", this.measureTableHeight);
        if (typeof ResizeObserver !== "undefined") {
            // El header crece al aparecer pills de filtro y desplaza el top de
            // la tabla → recalcular el cap. Observar el header (no el body)
            // evita un loop con el propio resize de la tabla.
            this.headerResizeObserver = new ResizeObserver(() => this.measureTableHeight());
            const headerEl = this.$refs.classificationHeader && this.$refs.classificationHeader.$el;
            if (headerEl) this.headerResizeObserver.observe(headerEl);
        }
    },
    beforeDestroy() {
        this.isActive = false;
        clearTimeout(this.searchTimer);
        this.stopSiiSyncPolling();
        window.removeEventListener("resize", this.measureTableHeight);
        if (this.headerResizeObserver) {
            this.headerResizeObserver.disconnect();
            this.headerResizeObserver = null;
        }
    },
    methods: {
        ...mapActions("classification", ["setPeriodDataSelect", "getCategories",
            "getAllAccounts", "getLedgerCounterpartsPage", "fetchLedgerCounterpartFacets",
            "fetchCounterpartSummary", "setSelectedPeriodCounterparts",
            "updateCounterpart", "updateSelectedType", "initClassificationFilters",
            "getIfHasNewDocs", "fetchCounterpart", "clearCounterparts"]),
        ...mapActions("uploadFiles", ["setNewSelectedTypeFile"]),
        ...mapActions("userAuth", ["getUserInfo"]),
        // Tick del polling del robot SII: refresca first_sync_completed
        // (user_info), la página de contrapartes y el resumen de los gráficos.
        // El watcher de siiSyncing corta el intervalo cuando llegan
        // contrapartes o el sync termina.
        async pollSiiSync() {
            if (this.user && this.user.email) {
                await this.getUserInfo({ email: this.user.email });
            }
            await Promise.all([this.getAllPaginatedCounterparts(), this.loadSummary()]);
        },
        startSiiSyncPolling() {
            if (this.siiSyncPoll) return;
            // Arrow para conservar el `this` del componente al disparar el timer.
            this.siiSyncPoll = setInterval(() => this.pollSiiSync(), SII_SYNC_POLL_MS);
        },
        stopSiiSyncPolling() {
            if (!this.siiSyncPoll) return;
            clearInterval(this.siiSyncPoll);
            this.siiSyncPoll = null;
        },
        downloadFile(drive_id, file_name) {
            this.isDownloadingFile = true
            if (!drive_id) return;
            downloadClassificationFile({
                rutCompany: this.user?.companies?.[0]?.rut || '',
                driveId: drive_id,
                fileName: file_name,
            }).finally(() => {
                this.isDownloadingFile = false
            })
        },
        updateSelectedAccounts(accountsSelected) {
            if (accountsSelected.length != 0) {
                this.filterTable = ''
            }
            this.accountFilters = [...accountsSelected]
            this.allFiltersToEntries()
        },
        updateSelectedCategories(categoriesSelected) {
            if (categoriesSelected.length != 0) {
                this.filterTable = ''
            }
            this.categoriesFilter = [...categoriesSelected]
            this.allFiltersToEntries()
        },
        clearFiltered(type) {
            if (type == 'accounts') {
                this.$refs.classificationHeader.clearAccountFilter()
            } else {
                this.$refs.classificationHeader.clearCategoryFilter()
            }
            this.allFiltersToEntries()
        },
        deleteItemFilter(type, item) {
            if (type == 'accounts') {
                this.$refs.classificationHeader.deleteAccountItem(item)
            } else {
                this.$refs.classificationHeader.deleteCategoryItem(item)
            }
        },
        // Mide el alto libre desde el top de la tabla hasta el viewport, menos la
        // paginación (que vive en el padre, debajo de la tabla) y el respiro
        // inferior. Resultado: header + tabla + paginación ≈ viewport, sin scroll
        // de página. `max-height` cap, no `height`: pocas filas no estiran.
        measureTableHeight() {
            const tableEl = this.$refs.counterpartTable && this.$refs.counterpartTable.$el;
            if (!tableEl) return;
            const top = tableEl.getBoundingClientRect().top;
            const paginationEl = this.$refs.pagination && this.$refs.pagination.$el;
            const paginationH = paginationEl ? paginationEl.offsetHeight : 0;
            const available = window.innerHeight - top - paginationH - TABLE_BELOW_GAP;
            this.tableMaxHeight = Math.max(TABLE_MIN_HEIGHT, Math.round(available));
        },
        onSelectedType(newValue) {
            const optionSelected = this.typeOptions.find((o) => o.value == newValue);
            this.updateSelectedType(optionSelected);
            // Los gráficos dependen del tipo elegido: se piden de nuevo.
            this.loadSummary();
            this.allFiltersToEntries();
        },
        onSelectedRange(newValue) {
            const optionSelected = this.periodDataSelect.find((o) => o.value == newValue);
            this.setSelectedPeriodCounterparts(optionSelected);
            this.actualPage = 1;
            this.loadFacets();
            this.loadSummary();
            this.getAllPaginatedCounterparts();
        },
        // Después de clasificar (modal, subida de documentos): la página, los
        // combos y los gráficos pueden haber cambiado.
        reloadAfterClassification() {
            this.getAllPaginatedCounterparts();
            this.loadFacets();
            this.loadSummary();
        },

        resetMsgs() {
            this.sortColumn = "total_3_months";
            this.sortDirection = "desc";
        },
        sortData(column, subtype = null, reset_msg = true) {
            if (reset_msg) {
                this.resetMsgs();
            }
            let col_name = column;
            if (subtype != null) {
                col_name = "attribute_" + subtype;
            }
            if (this.countDirectionOrder == 0) {
                this.sortColumn = "order";
                this.sortDirection = "asc";
            } else if (this.countDirectionOrder == 1) {
                this.sortColumn = col_name;
                this.sortDirection = "asc";
            } else if (this.countDirectionOrder == 2) {
                this.sortColumn = col_name;
                this.sortDirection = "desc";
            }
        },
        changeColumn(column, subtype = null) {
            let column_name = column;
            if (subtype) {
                column_name = column + "_" + subtype;
            }
            if (this.sortColumn != column_name) {
                this.countDirectionOrder = 0;
                this.sortColumn = column;
                if (subtype) {
                    this.sortColumn = column + "_" + subtype;
                }
            }
            this.countDirectionOrder = (this.countDirectionOrder + 1) % 3;
        },
        changeOrderGeneric(column, subtype = null) {
            this.changeColumn(column, subtype);
            this.sortData(column, subtype, false);
            // El orden es del servidor: se pide la primera página con el nuevo
            // criterio en vez de reordenar las 20 filas que hay en pantalla.
            this.actualPage = 1;
            this.getAllPaginatedCounterparts();
        },
        // Una PÁGINA de contrapartes del SII, ordenada y filtrada por el
        // servidor (decisiones §29, misma receta que la vista ERP). Antes pedía
        // `to: 999999` y resolvía orden, filtros, paginado y gráficos en JS:
        // con 211.195 contrapartes la consulta se cortaba a los 25 s.
        async getAllPaginatedCounterparts() {
            // Si la vista ya se destruyó (navegación a Libro Mayor), no dispares el
            // fetch: pisaría las contrapartes del ERP en el store compartido.
            if (!this.isActive) return;
            const { total, error, stale } = await this.getLedgerCounterpartsPage({
                ...this.serverFilters(),
                page: this.actualPage,
                page_size: this.itemsPerPage,
                order_by: this.serverOrderColumn,
                direction: this.sortDirection,
            });
            // Request reemplazado por uno más nuevo: no toca nada (el total
            // viejo pisaría el paginador con un número que ya no corresponde).
            if (stale) return;
            this.totalCounterparts = total;
            if (error) {
                notificate(`<strong>No se pudo cargar la lista</strong><br>${error}`,
                    "is-warning is-light");
            }
        },
        // Los filtros que viajan al servidor, iguales para la página y para
        // los combos: los dos tienen que ver el mismo conjunto de contrapartes.
        serverFilters() {
            return {
                ledger_only: false,
                from_day: toIsoDay(this.selectedPeriodCounterparts.from),
                to_day: toIsoDay(this.selectedPeriodCounterparts.to),
                search: this.searchFilter || "",
                counterpart_type: this.selectedType === "todos" ? "" : this.selectedType,
                accounts: this.accountFilters,
                categories: this.categoriesFilter,
                has_accrual: this.filterTableHasAccrual === "Devengados" ? true : null,
                unclassified_only: this.filterTable === "Sin Clasificar",
            };
        },
        // Los combos de cuentas y categorías: los sirve el backend porque el
        // front ya no tiene el dataset entero para derivarlos. Se piden con
        // los filtros activos, así ofrecen sólo lo que hay en la lista
        // visible (como cuando se derivaban de las filas filtradas en JS).
        async loadFacets() {
            if (!this.isActive) return;
            try {
                const facets = await this.fetchLedgerCounterpartFacets(this.serverFilters());
                // Request reemplazado por uno más nuevo: no toca los combos.
                if (!facets) return;
                this.allAccountsOnDocs = this.allAccounts.filter((account) =>
                    facets.accounts.includes(account.level_3)
                );
                this.allFilterCategories = facets.categories;
            } catch (error) {
                consoleHandlerError({ error, prefix: "[classification]", silent: true });
            }
        },
        // Los gráficos (contrapartes, documentos y montos clasificados / sin
        // clasificar) sobre TODAS las contrapartes del rango, con el tipo
        // elegido. El modal de bienvenida se decide con este resumen.
        async loadSummary() {
            if (!this.isActive) return;
            await this.fetchCounterpartSummary({
                from_day: toIsoDay(this.selectedPeriodCounterparts.from),
                to_day: toIsoDay(this.selectedPeriodCounterparts.to),
                counterpart_type: this.selectedType === "todos" ? "" : this.selectedType,
            });
            if (!this.hasShownFirstTimeModal
                && !isDismissedUntilEndOfDay("resumeClassification")
                && this.dataDocuments.notClassified > 0) {
                this.hasShownFirstTimeModal = true;
                this.showFirstTimeModal = true;
            }
        },
        // Consume los query params classify_* que deja el drawer de reportes
        // (DocumentsDetailCard.goToClassifier): abre el modal de la contraparte
        // con el rango que contiene al doc y le pide auto-expandir su árbol.
        // Los params se limpian SIEMPRE para que un refresh no re-dispare.
        async consumeClassifyDeepLink() {
            const query = this.$route.query;
            const ciId = Number(query.classify_ci_id);
            const tdId = Number(query.classify_td_id);
            if (!ciId || !tdId) return;
            try {
                const payOrCharge = query.classify_pay_or_charge || "C";
                const obligation = query.classify_obligation || "Otros";
                // initClassificationFilters despacha getCategories sin await —
                // re-despachar acá garantiza types listos antes de abrir.
                await this.getCategories();
                const data = await this.fetchCounterpart({
                    ciId, payOrCharge, obligation
                });
                const entry = data?.page_data?.[0];
                if (!entry) {
                    notificate(
                        `<strong>Atención</strong><br>
                        No se encontró la contraparte para clasificar.`,
                        "is-warning is-light"
                    );
                    return;
                }
                this.openAccountByCompanyModal(entry, {
                    rangeValue: resolveClassifierRange(query.classify_range, query.classify_doc_date, this.periodDataSelect),
                    autoOpenDocId: tdId,
                });
            } catch (error) {
                console.error("Error consuming classify deep-link:", error);
                notificate(
                    `<strong>Error</strong><br>
                    No se pudo abrir la clasificación del documento.`,
                    "is-danger is-light"
                );
            } finally {
                const cleaned = { ...this.$route.query };
                delete cleaned.classify_ci_id;
                delete cleaned.classify_td_id;
                delete cleaned.classify_pay_or_charge;
                delete cleaned.classify_obligation;
                delete cleaned.classify_doc_date;
                delete cleaned.classify_range;
                this.$router.replace({ query: cleaned }).catch(() => {});
            }
        },
        openAccountByCompanyModal(contraparte, opts = {}) {
            try {
                this.$buefy.modal.open({
                    ...this.modalDefault,
                    component: ClassifyAccountAttributes,
                    customClass: "classify_modal_width",
                    props: {
                        rangeOptions: this.periodDataSelect,
                        // El deep-link fuerza un rango que contiene al doc; el
                        // flujo normal sigue usando el rango de la pantalla.
                        rangeFromMainScreen: opts.rangeValue || this.selectedRange,
                        types: this.categories,
                        counterpart: contraparte,
                        autoOpenDocId: opts.autoOpenDocId || null,
                        // D10 (Ventas/Costos Devengados): la clasificación
                        // viene del archivo del import y es de solo lectura —
                        // el drawer se abre sin árbol editable (cubre también
                        // el deep-link, que pasa por acá).
                        readOnly: this.isConsolidatedCompany || isReadOnlyClassification(
                            contraparte && contraparte.counterpart_type
                        ),
                    },
                    events: {
                        "reload-counterpart": () => {
                            setTimeout(() => this.reloadAfterClassification(), 200);
                        },
                    },
                });
            } catch (err) {
                console.log("error?:", err);
            }
        },
        // Cambiar un filtro vuelve a pedir la primera página: filtrar es del
        // servidor desde que la lista se pagina.
        // Cambió un filtro: primera página y combos, en paralelo. Cambiar de
        // página no pasa por acá, así los combos no se recalculan al paginar.
        allFiltersToEntries() {
            this.actualPage = 1;
            this.loadFacets();
            return this.getAllPaginatedCounterparts();
        },

    },
    computed: {
        ...mapState("classification", ["periodDataSelect", "selectedPeriodCounterparts", "categories",
            "allAccounts", "allCounterparts", "allOrderedCounterparts", "selectedType",
            "selectedCounterpartObject", "dataCounterparts", "dataDocuments", "dataExpenses", "selectedRange", "typeOptions"]),
        ...mapState("uploadFiles", ["typeFiles"]),
        ...mapState("userAuth", ["user", "company"]),
        // Empresa acumulada activa: el clasificador entero pasa a solo
        // lectura (el backend igual bloquea con 403; esto evita el choque) y
        // las filas muestran su empresa de origen.
        isConsolidatedCompany() {
            return Boolean(this.company && this.company.is_consolidated);
        },
        // La lista que llega YA es la página: ni filtro ni corte en el cliente.
        filteredListCounterparts: function () {
            return this.allOrderedCounterparts;
        },
        maxPages: function () {
            return Math.ceil(this.totalCounterparts / this.itemsPerPage) || 1;
        },
        getPaginatedList: function () {
            return this.allOrderedCounterparts;
        },
        // Nombre de la columna que entiende el servidor: 'order' es "sin orden
        // elegido" y los atributos viajan como `attribute` (el tipo ya va en
        // el filtro), no como `attribute_<tipo>`.
        serverOrderColumn: function () {
            if (!this.sortColumn || this.sortColumn === "order") return "total_3_months";
            return this.sortColumn.startsWith("attribute_") ? "attribute" : this.sortColumn;
        },
        // Tabla vacía porque el primer sync del SII aún no terminó. El estado
        // vive en companies[].first_sync_completed (lo resuelve isUserSiiSyncing).
        siiSyncing: function () {
            return (
                this.getPaginatedList.length === 0 &&
                isUserSiiSyncing(this.user)
            )
        },
        getUrlDriveClassification: function(){
            return this.user?.companies?.[0]?.settings?.url_drive_file_reporting_classification || ""
        }
    },
    watch: {
        // Arranca el polling cuando el robot SII está visible y lo corta apenas
        // llegan contrapartes (getPaginatedList > 0) o el sync termina.
        siiSyncing: {
            immediate: true,
            handler(isSyncing) {
                if (isSyncing) {
                    this.startSiiSyncPolling();
                } else {
                    this.stopSiiSyncPolling();
                }
            },
        },
        // La búsqueda viaja al servidor: se espera a que el usuario deje de
        // tipear para no disparar una query por tecla.
        searchFilter() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.allFiltersToEntries(), SEARCH_DEBOUNCE_MS);
        },
        filterTable(newFilter) {
            if (newFilter) {
                this.$refs.classificationHeader.clearAccountFilter()
                this.$refs.classificationHeader.clearCategoryFilter()
            }
            this.allFiltersToEntries()
        },
        filterTableHasAccrual(){
            this.allFiltersToEntries()
        },
        actualPage() {
            this.getAllPaginatedCounterparts();
        },
        selectedUploadFile(newSelected) {
            if (newSelected != '') {
                this.setNewSelectedTypeFile(newSelected)
                this.$buefy.modal.open({
                    ...this.modalDefault,
                    component: ModalFullHeightRight,
                    customClass: "classify_modal_width",
                    props: {
                        headerComp: HeaderUploadDocs,
                        bodyComp: BodyUploadDocs,
                        headerProps: {
                        },
                        bodyProps: {
                        },
                    },
                    events: {
                        closed: () => {
                            this.selectedUploadFile = ''
                            this.reloadAfterClassification()
                            this.getIfHasNewDocs()
                        }
                    },
                });
            }
        }
    },
};
</script>

<style lang="scss" scoped>
.dashboard_classification {
    // Tokens scope-vista. Mismo dialecto ledger que Sidebar y AccountsPlan
    // (paper/rule/ink/accent) — la chrome no tematiza por tab. Los hijos
    // (ClassificationHeader, CounterpartTable) heredan estas vars en runtime.
    --paper: #ffffff;
    --paper-soft: #fafbfc;
    --rule: rgba(16, 24, 40, 0.08);
    --rule-strong: rgba(16, 24, 40, 0.14);
    --ink: #1d2939;
    --ink-muted: #475467;
    --ink-faint: #98a2b3;
    --accent: #0d5154;
    --accent-wash: rgba(13, 81, 84, 0.06);
    --hover: rgba(16, 24, 40, 0.03);
    --radius: 6px;

    background-color: var(--paper-soft);
    font-family: Inter, "Open Sans", sans-serif;
}

.underHeader {
    position: relative;
}

.main_div {
    // El outer main.dashboard reserva var(--app-sidebar-w) para el rail.
    // Acá sólo damos respiración de contenido.
    padding-left: clamp(16px, 2vw, 32px);
    padding-right: clamp(16px, 5vw, 90px);
    // padding-bottom: 32px;
}
</style>
<style>
.classify_modal_width .modal-background {
    background-color: rgba(0, 0, 0, 0.45) !important;
}

.classify_modal_width {
    min-width: 870px;
}


</style>
